import type { ChangeableSpotMarketFormula } from "../components/DeliveryOverview/ContractDeliveries/DeliveryModal/DeliveryForm/CustomFields/ChangeableSpotMarketField/ChangeableSpotMarketFormulaFieldStatic/ChangeableSpotMarketFormulaFieldStatic";
import type { ChangeablePrice } from "../components/DeliveryOverview/ContractDeliveries/SharedFields/ChangeablePriceField/ChangeablePriceField";
import { type RegulatoryDutyType } from "../components/Todos/hooks/useRegulatoryDuties";
import type {
  TodoSystemDecision,
  TodoUserDecision
} from "../components/Todos/Todos.types";
import type {
  components,
  GeneratorType,
  MixKind,
  RepresentationType,
  SupplierStatus,
  TransmissionSystemOperator,
  YesNoChoice
} from "../types/api.types";
import type { KeysToCamelCase } from "../types/caseHelpers.types";
import type {
  Frequency,
  MeasurementType,
  Medium,
  MspCategory,
  MspImportFrequency,
  SupplyType,
  Unit
} from "./enums";

export type User = components["schemas"]["User"];
export type ExtendedUser = components["schemas"]["ExtendedUser"];
export type CurrentUser = components["schemas"]["CurrentUser"];

export type ProjectMinimal = {
  id: string;
  name: string;
  variants: Array<VariantMinimal>;
};

export type Project = ProjectMinimal & {
  managers: Array<ExtendedUser>;
  customer: number;
  obersterVertriebspartner: string | null;
  reminderMailsActive: boolean;
  active: boolean;
  optichargeActive: boolean;
  sapExportActive: boolean;
  documentsInOnboardingAssistantActive: boolean;
  customerMbkWriteAccessActive: boolean;
  manualBlockRueckerstattungP6EegActive: boolean;
};

export type CreateManagerProject = {
  name: string;
  demo: boolean;
  obersterVertriebspartner: number;
};

type VariantId = number;
export type VariantMinimal = {
  id: VariantId;
  name: string;
};

export interface Variant extends VariantMinimal {
  bundleConsumers: boolean;
  disregardEegLevy: boolean;
}

export interface SiteBasic {
  id: number;
  global_id: string;
  name: string;
}

export interface ConsumerConsumptionShare {
  dateRangeConsumptionShare?: number | null;
  consumer: number;
  consumerName: string;
  consumptionPercentile?: string | null;
}

export interface DateRangeConsumptionShare {
  id?: number | null;
  firstDate: string | null;
  lastDate: string | null;
  consumerConsumptionShares: Array<ConsumerConsumptionShare>;
}
export interface ConsumptionShares {
  id?: number;
  isConfirmed?: boolean;
  meter?: number;
  dateRangeConsumptionShares?: Array<DateRangeConsumptionShare>;
}

export type Site = components["schemas"]["SiteResponse"];

export type SiteCategoriesSummaryResponse =
  components["schemas"]["SiteCategoriesSummaryResponse"];
export type SiteCategoriesResponse =
  components["schemas"]["SiteCategoriesResponse"];
export type SiteCategoriesUpdateRequest =
  components["schemas"]["SiteCategoriesUpdateRequest"];

export interface SiteCategoriesWithSiteFeatures extends SiteCategoriesUpdateRequest {
  invoicing_active: boolean;
}

export type WizardDetail = {
  displayPhase: boolean;
  done: boolean;
  name: string;
  objectsAreMissing: boolean;
  missingData: Array<WizardMissingData>;
  content: string;
  exclusiveOrFields: Array<Array<string>>;
};

type EnergyWizardDetail = WizardDetail & {
  blocked: boolean;
};

export type WizardMissingData = {
  componentName: MissingDataComponentName;
  id: number;
  missingDataType: MissingDataTypes;
  missingFields: Array<string>;
  name: string;
};

export enum MissingDataTypes {
  DEFAULT = "default",
  INVOICE = "invoice",
  REGULATORY = "regulatory",
  TAX = "tax",
  ANLAGE = "anlage",
  MEASUREMENT = "measurement",
  EEG = "eeg",
  KWK = "kwk",
  METERING_CONCEPT = "metering_concept",
  CONTRACT = "contract",
  POWER_OF_ATTORNEY_DATA = "power_of_attorney"
}

export enum MissingDataComponentName {
  PERSON = "Person",
  GENERATOR = "Generator",
  GENERATION_SHARE = "GenerationShare",
  METER = "Meter",
  CONNECTION = "Connection",
  SITE = "Site",
  DELIVERY = "Delivery",
  CONSUMER = "Consumer",
  MARKET_LOCATION = "MarketLocation",
  METERING_LOCATION = "MeteringLocation",
  MIETERSTROM_CONTRACT = "MieterstromContract"
}

export type VollmachtDetail = WizardDetail & {
  hasMissingDocuments: boolean;
};

export interface SiteEnergyWizardPhases {
  meters: EnergyWizardDetail;
}

export type SiteEnergyWizard = {
  id: number;
  enabled: boolean;
  phases: SiteEnergyWizardPhases;
};

export type OnboardingAssistantDocument = {
  label: string;
  todoFiles: Array<TodoFile>;
};
export type OnboardingAssistantDocuments = Array<OnboardingAssistantDocument>;
export type SiteWizardOnboardingStatus = {
  onboardingDone: boolean;
  todoStatus: {
    regulatoryTodo: boolean;
    msbDataTodo: boolean;
    mbkTodo: boolean;
  };
};

export interface SiteWizardPhases {
  configuration: WizardDetail;
  msbauthority: VollmachtDetail;
  dataloadauthority: VollmachtDetail;
  regulatory: WizardDetail;
  vermarktungContracts: WizardDetail;
  p6Contracts: WizardDetail;
  invoices: WizardDetail;
  meteringConcept: WizardDetail;
}

export type SiteWizard = {
  id: number;
  enabled: boolean;
  onboardingDone: SiteWizardOnboardingStatus;
  phases: SiteWizardPhases;
};

export type SitePlausibility =
  components["schemas"]["SiteMeteringPlausibilityResponse"];

export type MeterPlausibility =
  components["schemas"]["MeteringPlausibilityResponse"];

export interface Component {
  id: number;
  name: string;
}

export enum DeliveryType {
  DirectDelivery = "direct_delivery",
  ForwardingDelivery = "forwarding_delivery"
}

export enum PriceModel {
  PassingOn = "passing_on",
  FixedPrice = "fixed_price"
}

export interface Delivery extends Omit<Component, "id"> {
  id: string;
  automaticallyDetermineGridFees: boolean;
  identificationGraphic: string | null;
  concessionFees: Array<ChangeablePrice>;
  contract: string;
  deliveryKind: DeliveryType;
  electricityTax: number | null;
  generators: Array<number>;
  lossesTransfer: boolean;
  gridUsageBillingModel: string;
  gridUsagePowerPrices: Array<ChangeablePrice>;
  priceModel: PriceModel;
  site: number | null;
  workingPrices: Array<ChangeablePrice>;
  workingPricesPrognosis: Array<ChangeablePrice>;
  mixKind: MixKind;
  greenCountries: string | null;
  nuclearPercentage: number | null;
  coalPercentage: number | null;
  gasPercentage: number | null;
  otherFossilPercentage: number | null;
  tenantPercentage: number | null;
  renewableNotPromotedPercentage: number | null;
  renewablePromotedPercentage: number | null;
  yearMix: number | null;
  emissionCo2: number | null;
  radioactiveWaste: number | null;
  useSpotMarketPrices: boolean;
  minimumPurchaseQuantityApplicable: boolean;
  minimumPurchaseQuantity: number | null;
  spotMarketPrices: Array<ChangeableSpotMarketFormula>;
}

interface ActiveCompany {
  id: number;
  name: string;
}
interface SiteCompanies {
  activeCompanies: Array<ActiveCompany>;
  id: number;
}
export interface ActiveCompanies {
  id: number;
  sites: Array<SiteCompanies>;
}

export interface Participation {
  id: number;
  value: boolean;
  firstDate: string | null;
  lastDate: string | null;
}

export interface VersorgerstatusPeriod {
  id?: number;
  status: SupplierStatus;
  firstDate: string | null;
  lastDate: string | null;
}

export interface Person extends Component {
  address: string;
  addressExtra: string;
  besondereAusgleichsregelungEeg: number | null;
  besondereAusgleichsregelungKwk: number | null;
  besondereAusgleichsregelungOffshore: number | null;
  city: string;
  color: string;
  companyNumber: string;
  contact: string | null;
  customsOffice: string | null;
  dinEn16247_1Installed: string;
  dinEnIso_50001Installed: string;
  electricityConsumptionOver_50GwhPerYear: string | null;
  electricityOrEnergyTaxRelief_10StromstgOr_55Energiestg: string | null;
  emasInstalled: string;
  energyTaxRelief_51Energiestg: string | null;
  energyTaxRelief_54Energiestg: string | null;
  forestryOrAgriculture: string;
  generalPartnerName: string | null;
  generalPartnerCity: string | null;
  generalPartnerCourt: string | null;
  generalPartnerRegisterNumber: string | null;
  generalPartnerCeo: string | null;
  invoiceRecipient: string;
  invoiceSuppliedAddressIsGeneralAddress: YesNoChoice | null;
  invoiceSuppliedAddress: string | null;
  invoiceSuppliedAddressExtra: string | null;
  invoiceSuppliedBank: string | null;
  invoiceSuppliedBic: string | null;
  invoiceSuppliedCity: string | null;
  invoiceSuppliedDebitNumber: string | null;
  invoiceSuppliedAccountingArea: string | null;
  invoiceSuppliedTargetAccountingArea: string | null;
  invoiceSuppliedBusinessArea: string | null;
  invoiceSuppliedCostCenter: string | null;
  invoiceSuppliedPspElement: string | null;
  invoiceSuppliedProfitCenter: string | null;
  invoiceSuppliedGlobalId: string | null;
  invoiceSuppliedInformationCategory: string | null;
  invoiceSuppliedInformation: string | null;
  invoiceSuppliedCustomerNumber: string | null;
  invoiceSuppliedEmail: string | null;
  additionalInvoiceSuppliedEmails: Array<{ emailAddress: string }>;
  invoiceSuppliedBusinessReference: string | null;
  invoiceSuppliedIban: string | null;
  invoiceSuppliedZip: string | null;
  invoiceSupplierBank: string | null;
  invoiceSupplierBic: string | null;
  invoiceSupplierCeo: string | null;
  invoiceSupplierCourt: string | null;
  invoiceSupplierCreditorId: string | null;
  invoiceSupplierEmail: string | null;
  invoiceSupplierFax: string | null;
  invoiceSupplierIban: string | null;
  invoiceSupplierLocation: string | null;
  invoiceSupplierLogo: string | null;
  invoiceSupplierSalesTaxId: string | null;
  invoiceSupplierTaxNumber: string | null;
  invoiceSupplierPhone: string | null;
  invoiceSupplierIsGbr: YesNoChoice | null;
  invoiceSupplierRegisterNumber: string | null;
  invoiceSupplierTaxId: string | null;
  invoiceSupplierUrl: string | null;
  isErloesmonitoringActive: boolean;
  isInMieterstromProject: boolean;
  isInternalSupplier: string;
  isRedispatchImportActive: boolean;
  isRegisteredAsEltvuAtAmprion: string | null;
  isRegisteredAsEltvuAtHertz: string | null;
  isRegisteredAsEltvuAtTennet: string | null;
  isRegisteredAsEltvuAtTransnet: string | null;
  isStrompbgCalculatorActive: boolean;
  isParagraph_6EegActive: boolean;
  isInvoicingActive: boolean;
  isYearlyReportOfTaxReliefsRequired: string | null;
  makesUseOfBeihilfenStrompreiskompensation: string | null;
  makesUseOfBesondereAusgleichsregelung: string | null;
  makesUseOfExemptsOver_500_000PerYear: string | null;
  manufacturingIndustry: string;
  mastrNummerMarktakteur: string | null;
  unternehmenInSchwierigkeitenYears: Array<string>;
  participationsInMieterstromModel?: Array<Participation>;
  pensionSalariesPaid: number;
  taxFormsRepresentationType: RepresentationType | null;
  taxFormsCompanyRepresentedBy: number | null;
  shouldRegulatoryDutiesBeChecked: boolean;
  isSolarContractor: boolean;
  sitesWhereActive?: Array<{ id: number; name: string }>;
  smallMediumEnterprise: string | null;
  taxFormsSubmissionInOwnName: boolean;
  versorgerstatusPeriods?: Array<VersorgerstatusPeriod>;
  supplyElectricityTaxMonthly: string;
  taxFormsApplicantStreet: string | null;
  taxFormsApplicantHouseNumber: string | null;
  taxFormsApplicantAddressSupplement: string | null;
  taxFormsApplicantLegalStructure: string | null;
  taxFormsApplicantDateOfBirthOrFoundation: string | null;
  taxFormsApplicantName: string | null;
  taxFormsApplicantOtherCity: string | null;
  taxFormsApplicantResidenceOrOffice: string | null;
  taxFormsApplicantZip: string | null;
  taxFormsApplicantCity: string | null;
  taxFormsApplicantCityDistrict: string | null;
  taxFormsApplicantCountry: string | null;
  taxFormsApplicantNumber: string | null;
  taxFormsApplicantTransactionNumberElectricityTax: string | null;
  taxFormsBankAccountBic: string;
  taxFormsBankAccountHolder: string;
  taxFormsBankAccountIban: string;
  taxFormsContactEmail: string;
  taxFormsContactFax: string;
  taxFormsContactName: string;
  taxFormsContactPhone: string;
  taxFormsContactWebsite: string;
  variant: number;
  zip: string;
}

export type OperatorListItem = components["schemas"]["OperatorListItem"];

export type GeneratorOverview = components["schemas"]["GeneratorOverview"];
export interface Generator extends Component {
  anlageStromstg: number;
  approvalDate: string | null;
  buildingInstalled: string | null;
  commissioning: string | null;
  connection: number;
  decommissioning: string | null;
  directMarketingFee: number | null;
  marketPremiumViaTendering: boolean | null;
  einheit: string | null;
  eligibleForNonBuildingInstalledFeedinTariff: string | null;
  feedinTariffDeprecated: number | null;
  feedinTariffNonPv: number | null;
  applicableValuePv: number | null;
  marketValueEeg: number | null;
  initialInstalledCapacity: number | null;
  installedCapacity: number;
  isCombined: boolean;
  kwkgDirectDeliverySubsidy: string | null;
  modernized: string | null;
  operatorHasChangedSince_01_01_2017: string | null;
  ownConsumptionBefore_2014_08_01: string | null;
  ownConsumptionBefore_2015_01_01: string | null;
  ownConsumptionSubsidy: string | null;
  person: number;
  registeredForMieterstrom: string | null;
  residentialBuildingInstalled: string | null;
  site: number;
  siteName: string;
  type: GeneratorType;
  typeLabel: GeneratorTypeLabel;
  yearlyEnergy: number;
  isRedispatchImportActive: boolean;
  isRedispatchImportActiveForOperator: boolean;
  trId?: string;
}

export enum GeneratorTypeLabel {
  EEGAnlageSolar = "Photovoltaik",
  EEGAnlageBiomasse = "Biomasse (fest, flüssig, gasförmig) - EEG",
  EEGAnlageWind = "Windkraft",
  EEGAnlageWasser = "Wasserkraft",
  EEGAnlageGSGK = "Geothermie, Solarthermie, Grubengas, Klärschlamm (GSGK) - EEG",
  KWKAnlageVerbrennung = "KWK fossil (u. a. Erdgas-BHKW)",
  KWKAnlageBiomasse = "Biomasse - nicht EEG",
  KWKAnlageGSGK = "Geothermie, Solarthermie, Grubengas, Klärschlamm (GSGK) - nicht EEG",
  Sonstige = "Sonstige"
}

export interface CreatedGenerator {
  generators: Array<Generator>;
  warnings: Array<string>;
}

export interface Consumer extends Component {
  connection: number;
  electricity_price: number;
  eligibleForTaxReliefForParticularProcesses: "yes" | "no";
  operational_purpose: "yes" | "no";
  person: number;
  site: number;
  type: string;
  type_label: string;
  yearlyEnergy: number;
}

export interface Storage extends Component {
  commissioning: string | null;
  connection: number;
  decommissioning: string | null;
  efficiency: number | null;
  electricityPrice: number; // Jan M. said this is EE+ only and can be removed already
  eligibleForTaxReliefForParticularProcesses: string; // Jan M. said this is Analyzer only and can be removed
  installedCapacity: number | null;
  person: number;
  site: number;
  standbyConsumption: number;
  storageSize: number;
  usageStrategy: string | null; // Jan M. said this is EE+ only and can be removed already
}

export enum MeteringDirection {
  VerbrauchEntnahme = "consumption_feedout",
  Zweirichtungszähler = "bidrectional",
  ErzeugungEinspeisung = "generation_feedin",
  VierQuadrantenZähler = "four_quadrant"
}

export interface TextBlockElement {
  id: number;
  site: number;
  text: string;
  x: number;
  y: number;
  fontWeight: number;
  fontSize: number;
}
export interface ContainerElement {
  color: string;
  height: number;
  id: number;
  site: number;
  width: number;
  data: {
    type: string;
    color: string;
  };
  x: number;
  y: number;
}

export type MarktakteurContact = Omit<
  KeysToCamelCase<components["schemas"]["MarktakteurContact"]>,
  "marktrollen"
> & {
  marktrollen: Array<
    KeysToCamelCase<components["schemas"]["MarktrolleContact"]>
  >;
};

export interface Connection extends Component {
  connectingParty: number | null;
  ghostNode: GhostNode | null;
  gridOperator: number;
  gridOperatorMarktakteur: MarktakteurContact | null;
  gridOperatorName: string;
  site: number;
  supplyContractHeldBy: number | null;
  transmissionSystemOperator: TransmissionSystemOperator | null;
  transmissionSystemOperatorMarktakteur: MarktakteurContact | null;
  voltage: string;
  meteringConcept: number | null;
  supplyType: SupplyType | null;
}

export interface Meter extends Component {
  calibrationValidUntil: string | null;
  conversionFactor: number;
  frequency: Frequency;
  isCalibrated: "yes" | "no";
  consumptionShare: number | null;
  locationFeedin: number | null;
  locationFeedout: number | null;
  measurementType: MeasurementType;
  medium: Medium;
  meteringDirection: MeteringDirection;
  meteringDirectionLabel: string;
  number: number | null;
  site: number;
  subMeteringSystem: string;
  globalId: string;
}

export interface GhostNode {
  site: number;
  id: number;
  name: string | null;
}

export interface MeteringServiceProvider {
  id: number;
  marktpartneridentifikationsnummer: string;
  medium: string | null;
  name: string;
  category: MspCategory;
  email: string | null;
  notes: string | null;
  importFrequency: MspImportFrequency;
  customLabel: string | null;
}

export interface Todo {
  category: TodoCategory;
  cluster: RegulatoryCluster;
  priority: Priority;
  description: string | null;
  status: TodoStatus;
  dueDate: string | null; // "31.12.2020"
  fulfillFor: string | null;
  id: number;
  label: string;
  overdue: boolean;
  person: string | null;
  project: string;
  regulatoryDuty: RegulatoryDutyType | null;
  responsible: number | null;
  systemCreated: boolean;
  systemDecision: TodoSystemDecision | null;
  todoFiles: Array<TodoFile>;
  todoLogs: Array<TodoLog>;
  userDecision: TodoUserDecision;
  variant: number;
  documentProvided: boolean;
  group: TodoGroup;
}

export enum TodoGroup {
  ProjectPreparation = "project_preparation",
  RegulatoryDuties = "regulatory_duties"
}

export interface VollmachtFiles {
  id: number;
  todoFiles: Array<TodoFile>;
}

export enum TodoCategory {
  Onboarding = "onboarding",
  RegulatoryDuty = "regulatory_duty",
  Other = "other"
}

export enum RegulatoryCluster {
  Onboarding = "onboarding",
  RegulatoryDuty = "regulatory_duty",
  Other = "other",
  AssessmentProfitabilityAndFeasibility = "assessment_profitability",
  PlanningAndRealisation = "planning_and_realisation",
  MeteringConceptAndOperations = "metering_concept_and_ops",
  DirectMarketing = "direct_marketing",
  PowerSupplyToTenantAndCustomer = "power_supply_to_tenant",
  ProjectOverview = "project_finalisation"
}

export enum TodoStatus {
  Open = "open",
  Done = "done",
  NotRelevant = "not_relevant"
}

export enum Priority {
  High = "high",
  Medium = "medium",
  Low = "low",
  Unknown = "unknown"
}

export interface TodoFile {
  createdBy: ExtendedUser | null;
  creationDatetime: string; // "23.03.2021 19:04:20"
  id: number;
  name: string;
  revoked?: string | null;
}

export interface TodoLog {
  creationDatetime: string; // "22.03.2021 17:08:28"
  description: string;
  user: string;
}

export type TodoNote = components["schemas"]["TodoNote"];

export type GeneratorDataStatus =
  "overwriteSuccess" | "overwriteFail" | "getPossibleChangesFail";

export interface RegulatoryDutyEvent {
  title: string;
  rrule: string;
}

export interface ReversalInvoice {
  id: string;
  file: string;
  state: InvoiceState;
}

export interface InvoiceResponse {
  invoiceData?: InvoiceData;
  file: string;
  id: string;
  state: InvoiceState;
  project: string;
  sentViaEmail: boolean;
  reversalInvoice?: ReversalInvoice | null;
}

export interface InvoiceData {
  billingPeriodEnd: string; // e.g. "31.12.2021"
  billingPeriodStart: string; // e.g. "31.12.2021"
  created: string; // e.g. "31.12.2021 19:04:20"
  contract: string;
  id: string;
  number: string;
  site: number;
  isAdvanceInvoice: boolean;
  isCorrectionInvoice: boolean;
}

// Corresponds to class paragraph_6_eeg.enums.CreditState
export enum InvoiceState {
  ReadyToSend = "ready_to_send",
  Cancelled = "cancelled",
  Sent = "sent",
  Paid = "paid"
}

export enum CancellationState {
  ReadyToSend = "ready_to_send",
  Sent = "sent",
  Received = "received"
}

export interface AdvancePaymentsPlan {
  id: string;
  project: string;
  file: string;
  advancePaymentsPlanData: AdvancePaymentsPlanData;
}

export interface AdvancePaymentsPlanData {
  id: string;
  contract: string;
  site: number;
  paymentPlanBegin: string;
  paymentPlanEnd: string;
}

export interface MeteringOrMarketLocation extends Component {
  comment: string;
  commissioning: string | null;
  customLabel: string | null;
  decommissioning: string | null;
  frequency: Frequency;
  measurementType: MeasurementType;
  measurands: Array<string>;
  medium: Medium;
  meteringServiceProvider: number | null;
  locationDataReception: string;
  number: string;
  site: number;
  status: "active" | "inactive";
  updated: string;
}

export interface MeteringOrMarketLocationSuggestedCommissionPeriodResponse {
  commissioning: string | null;
  decommissioning: string | null;
}

export interface SuggestedMeteringOrMarketLocationResponse {
  id: number;
}

export enum NotificationSettingDbName {
  TodoCreation = "receiveEmailOnTodoCreation",
  TodoUpcomingDueDate = "receiveEmailOnTodoUpcomingDueDate"
}

export type NotificationSetting = {
  dbName: NotificationSettingDbName;
  label: string;
};

export type NotificationSettings = Record<NotificationSettingDbName, boolean>;

export interface Paged<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface TemplateMeter {
  id: number;
  name: string;
  defaultUnit: Unit;
  siteName: string;
  units: Array<[Unit, string]>;
}

export interface ParameterSet {
  id: string;

  name: string;
  originName?: string;
}

export enum MachineAccountReimportStatus {
  InProgress = "IN_PROGRESS",
  Success = "SUCCESSFUL",
  Error = "FAILED"
}

export interface MachineAccountConfiguration {
  id: number;
  parameterSet: string;
  name: string;
  receiverUuid: string;
  email?: string;
  parameterSetName?: string;
  status: MachineAccountReimportStatus;
  celeryTaskId: string;
}

export interface AnlageStromStg {
  id: number;
  variant: number;
  bezeichnung: string | null;
  datumErlaubnisBetriebserklaerung: string | null;
  datumErlaubnisStfreiParagraph_9Abs_1Nr_1Stromstg: string | null;
  einzelerlaubnisSteuerfreieEntnahmeZurStromerzeugung: string;
  hauptzollamtAnlagennummer: number | null;
  nummerErlaubnisBetriebserklaerung: number | null;
  stfreiParagraph_9Abs_1Nr_1Stromstg: string | null;
  datumEinzelerlaubnisSteuerfreieEntnahmeZurStromerzeugung: string | null;
  erlaubnisnummerSteuerfreieEntnahme: string | null;
  steuerbefreiungStromZurStromerzParagraph_9Abs_2Stromstg: string | null;
  stfreiParagraph_9Abs_1Nr_3Stromstg: string | null;
  datumErlaubnisStfreiParagraph_9Abs_1Nr_3Stromstg: string | null;
  hasStandortuebergreifendeVerklammerung: boolean | null;
}

export interface MastrData {
  adresszusatz: string | null;
  anschlussAnHoechstOderHochSpannung: boolean;
  auflageAbschaltungLeistungsbegrenzung: boolean;
  beschreibung: string | null;
  breitengrad: number | null;
  bruttoleistung: number | null;
  bundesland: string | null;
  datumEndgueltigeStilllegung: string | null;
  datumErstmaligeInbetriebnahme: string | null;
  datumLetzteAktualisierung: string | null;
  eeganlage: string;
  einheitMastrNummer: string | null;
  einsatzverantwortlicher: string | null;
  einspeisungsart: string | null;
  fernsteuerbarkeitDv: boolean;
  fernsteuerbarkeitNb: boolean;
  flurFlurstuecknummern: string | null;
  gaussKruegerHoch: number | null;
  gaussKruegerRechts: number | null;
  gemarkung: string | null;
  gemeinde: string | null;
  gemeindeschluessel: string | null;
  geplantesInbetriebnahmedatum: string | null;
  hausnummer: number | null;
  hersteller: string;
  herstellerEinheit: string | null;
  id: number;
  imMastrRegistriert: boolean;
  inbetriebnahmedatum: string | null;
  inselbetriebsfaehigkeit: string | null;
  kraftwerksnummer: string | null;
  laengengrad: number | null;
  windAnLandOderSee: string | null;
  land: string | null;
  landkreis: string | null;
  meldedatum: string | null;
  nameStromerzeugungseinheit: string | null;
  nameWindpark: string | null;
  ort: string | null;
  postleitzahl: string | null;
  praequalifiziertFuerRegelenergie: string | null;
  schwarzstartfaehigkeit: string | null;
  seriennummer: string | null;
  strasse: string | null;
  typenbezeichnung: string | null;
  utmEast: number | null;
  utmNorth: number | null;
  utmZonenwert: number | null;
  weic: number | null;
  weicDisplayName: string | null;
  zubauAustausch: string | null;
}

export interface VBHCalculated {
  status: "FAILED" | "SUCCESS";
  value: number;
  message?: string;
}
export interface VBHCalculatedResponseData {
  [key: number]: VBHCalculated;
}

export interface GeneratorChangesFromMastr {
  fieldName:
    | "lokation_mastr_nummer"
    | "registrierungsdatum"
    | "anlagenbetreiber_mastr_nummer";
  fieldVerboseName: string;
  oldValue: string | null;
  newValue: string | null;
  modelName: string;
  modelApp: string;
  objId: number;
}
export interface MetersSuggestions {
  subMeteringEntityId: number;
  suggestedMeterId: number;
}

export interface AnlageStromstgListData {
  name: string;
  id: number;
  bruttoleistung: number | null;
  site: string;
}

export interface Direktvermarkter {
  id: number;
  name: string;
}

export enum Measurand {
  negative = "negative",
  positive = "positive",
  reactiveCapacitive = "reactiveCapacitive",
  reactiveInductive = "reactiveInductive",
  lossOfWork = "lossOfWork"
}
export interface MeasurandDataGaps {
  gaps: Array<Array<string>>;
  measurand: Measurand;
}

export type DataGapKeyPattern = `${number}.${string}`;

export interface DataGaps {
  expectedOrigin: string;
  [key: DataGapKeyPattern]: MeasurandDataGaps;
}

export type HistoryLog = components["schemas"]["HistoryLog"];
export type HistoryLogs = Array<HistoryLog>;
export type HistoryChanges = components["schemas"]["Changes"];
export type HistoryChange = HistoryChanges[keyof HistoryChanges];
export type HistoryDateBoundedValue = components["schemas"]["DateBoundedValue"];

export type MeteringConceptTodo = components["schemas"]["TodoLabel"];
export type TodosCanGenerateDocuments =
  components["schemas"]["TodosCanGenerateDocuments"];
